import _extends from '@babel/runtime/helpers/extends';
import React__default, { useEffect, isValidElement, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { u as useInlinePopover } from './useInlinePopover-D_iJ07xC.js';
import { P as PopoverTrigger } from './Popover.styles-C9PqLyFY.js';
import { P as PopoverContainer } from './PopoverContainer-DUYc_Bvx.js';
import { P as PopoverContentContainer } from './PopoverContentContainer/PopoverContentContainer-BFztHrE7.js';
import { u as useIsMobile } from '../theme/MobileThemeProvider/useIsMobile-B-VP6bHU.js';
import { g as getDefaultPopoverSpacing } from './utils-Cmlx81uL.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';
import { i as isEventFromWithinElement } from '../LocalModules/storm-ui-utils/dist/esm/isEventFromWithinElement-BvBeBwSW-BvBeBwSW.js';
import { t as trapTabFocus } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/trapTabFocus-vpg0lK2r-Bk1xQ7S4.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { f as focusFirstElement } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/focusFirstElement-C1TqGpwr-LXfVb-eG.js';
import { T as TaktIdProvider } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';

const Popover = ({
  align = 'center',
  autoFocus = false,
  boundaryRef,
  children,
  closeButtonLabel = 'Close Popover',
  disableFlip = false,
  disablePortal,
  isOpen,
  onClick,
  onCloseButtonClick = noop,
  padding = 'base',
  popoverRef,
  portalRef,
  position = 'top',
  shadowRoot,
  skidding = 0,
  spacing,
  strategy = 'absolute',
  taktId,
  taktValue,
  transitionDuration,
  trigger,
  fullWidthTrigger,
  variant = 'default',
  withCloseButton = false,
  withArrow = false,
  ...rest
}) => {
  var _useIsMobile;
  const placement = align === 'center' ? position : `${position}-${align}`;
  const isMobile = (_useIsMobile = useIsMobile()) !== null && _useIsMobile !== void 0 ? _useIsMobile : false;
  const theme = /*#__PURE__*/useTheme();
  const {
    attributes,
    styles,
    triggerElement,
    popoverElement,
    arrowElementRef,
    setPopoverElement,
    setTriggerElement,
    update
  } = useInlinePopover({
    offsetDistance: spacing !== null && spacing !== void 0 ? spacing : getDefaultPopoverSpacing(isMobile, theme),
    offsetSkidding: skidding,
    placement,
    enableFlip: !disableFlip,
    preventOverflow: {
      mainAxis: true
    },
    boundaryRef,
    strategy,
    withArrow
  });
  useEffect(() => {
    if (! /*#__PURE__*/isValidElement(trigger) && trigger && 'current' in trigger) {
      if (trigger !== null && trigger !== void 0 && trigger.current) {
        setTriggerElement(trigger.current);
      }
    }
  }, [setTriggerElement, trigger]);

  /**
   * Handles mouse clicks on the popover close button.
   */
  const handleCloseClicked = useCallback(event => {
    event.stopPropagation();

    // call the callback function passed via props.
    onCloseButtonClick(event);
  }, [onCloseButtonClick]);

  /**
   * Click on outside popover and trigger should close the popover
   */
  const handleDocumentMousedown = useCallback(event => {
    if (typeof onCloseButtonClick === 'function' && !isEventFromWithinElement(event, popoverElement) && !isEventFromWithinElement(event, triggerElement)) {
      /** TODO: Fix this type in Storm 4.x */
      onCloseButtonClick(event);
    }
  }, [onCloseButtonClick, popoverElement, triggerElement]);

  /**
   * Handles keyboard KeyDown input when the popover surface is in focus.
   */
  const handleKeyDown = useCallback(event => {
    const {
      key
    } = event;
    if (autoFocus && popoverElement) {
      // Trap the focus of the browser inside the popover element. Keep the user in this element
      // and allow them to tab through content until the user closes the <Popover />.
      trapTabFocus(event, popoverElement, false);
    }
    if (key === KEYBOARD_KEYNAMES.SPACE) {
      event.stopPropagation();
    }
  }, [autoFocus, popoverElement]);

  /**
   * Handles keyboard input when the popover surface is in focus.
   */
  const handleKeyUp = useCallback(event => {
    const {
      key
    } = event;

    // Close the popover if the Escape key is pressed
    if (key === KEYBOARD_KEYNAMES.ESCAPE) {
      event.stopPropagation();
      onCloseButtonClick(event);
    }
  }, [onCloseButtonClick]);

  /**
   * Catches the click event from the popover trigger and only
   * call the 'onClick' callback if it was passed.
   */
  const preventTriggerClickThrough = useCallback(event => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
  }, [onClick]);

  /**
   * Function called once the <Transition /> component is in the entered state.
   *
   * If we are showing focus because we have had some keyboard input, we should set
   * focus to the Popover element.
   */
  const handlePopoverEntered = useCallback(() => {
    if (autoFocus && popoverElement) {
      focusFirstElement(popoverElement);
    }
  }, [autoFocus, popoverElement]);
  const handlePopoverEntering = useCallback(isAppearing => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);

  /**
   * Function called once the <Transition /> component is in the exited state.
   */
  const handlePopoverExited = useCallback(() => {
    if (autoFocus && triggerElement) {
      focusFirstElement(triggerElement);
    }
  }, [autoFocus, triggerElement]);
  return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('popover')
  }, /*#__PURE__*/isValidElement(trigger) && /*#__PURE__*/React__default.createElement(PopoverTrigger, {
    onClick: preventTriggerClickThrough,
    ref: setTriggerElement,
    $fullWidth: fullWidthTrigger
  }, trigger), /*#__PURE__*/React__default.createElement(PopoverContainer, _extends({}, rest, {
    attributes: attributes,
    disablePortal: disablePortal,
    handleKeyDown: handleKeyDown,
    handleKeyUp: handleKeyUp,
    handlePopoverEntered: handlePopoverEntered,
    handlePopoverEntering: handlePopoverEntering,
    handlePopoverExited: handlePopoverExited,
    isOpen: isOpen,
    onDocumentMousedown: handleDocumentMousedown,
    position: position,
    setPopoverElement: setPopoverElement,
    shadowRoot: shadowRoot,
    styles: styles,
    variant: variant,
    portalRef: portalRef,
    popoverRef: popoverRef,
    transitionDuration: transitionDuration,
    withArrow: withArrow,
    arrowElementRef: arrowElementRef
  }), /*#__PURE__*/React__default.createElement(PopoverContentContainer, {
    closeButtonLabel: closeButtonLabel,
    handleCloseClicked: handleCloseClicked,
    padding: padding,
    variant: variant,
    withCloseButton: withCloseButton
  }, children)));
};

export { Popover as P };
//# sourceMappingURL=Popover-CC_x_sCZ.js.map
